/**
 * useStoreStatusMonitoring Composable
 * Handles periodic monitoring of store status (open/closed/schedule_only)
 * Single Responsibility: Store status monitoring
 * Compatible with Vue 2
 */

/**
 * Composable for monitoring store status periodically
 * @param {Object} store - Vuex store instance
 * @param {Object} selectedStoreComposable - useSelectedStore composable instance
 * @returns {Object} Store status monitoring methods
 */
export function useStoreStatusMonitoring(store, selectedStoreComposable) {
  if (!store) {
    console.warn("useStoreStatusMonitoring: Store instance is required");
    return {
      checkStoreStatus: () => {},
      startMonitoring: () => {},
      stopMonitoring: () => {}
    };
  }

  let statusCheckInterval = null;
  const CHECK_INTERVAL_MS = 60000; // Check every minute

  /**
   * Checks if restaurant is closed using the selected store composable
   * Updates store state with the result
   */
  function checkStoreStatus() {
    if (!selectedStoreComposable) {
      return;
    }

    const status = selectedStoreComposable.getStoreStatus();
    // Update store state: closed if status is 'closed', otherwise false
    const isClosed = status === "closed";
    store.commit("setIsStoreClosed", isClosed);
  }

  /**
   * Starts periodic monitoring of store status
   * @param {number} intervalMs - Interval in milliseconds (default: 60000)
   */
  function startMonitoring(intervalMs = CHECK_INTERVAL_MS) {
    // Check immediately
    checkStoreStatus();

    // Set up interval to check store status periodically
    statusCheckInterval = setInterval(checkStoreStatus, intervalMs);
  }

  /**
   * Stops periodic monitoring of store status
   */
  function stopMonitoring() {
    if (statusCheckInterval) {
      clearInterval(statusCheckInterval);
      statusCheckInterval = null;
    }
  }

  return {
    checkStoreStatus,
    startMonitoring,
    stopMonitoring
  };
}
